import { useEffect } from 'react';
import { useObserver } from 'mobx-react-lite';

import useUser from 'src/hooks/use-user';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useCart from 'src/hooks/use-cart';
import { useGetRewards } from 'src/components/loyalty/use-get-rewards';
import { setLoyaltyData } from 'src/components/loyalty/loyalty.helpers';
import { useKioskPro } from './use-kiosk-pro';

export type KioskLoyaltyStatus = 'available' | 'error' | 'loading' | 'unavailable' | 'validation_required';
type LoyaltyProvider = 'Alpine IQ' | 'Dutchie Loyalty' | 'Springbig' | null;

type UseLoyaltyReturn = {
  loyaltyPoints: string;
  loyaltyStatus: KioskLoyaltyStatus;
  loyaltyProvider: LoyaltyProvider;
  isAlpineIQEnabled: boolean;
  isDutchiePOSLoyaltyEnabled: boolean;
  isSpringbigEnabled: boolean;
};

function getLoyaltyProvider({
  alpine,
  dutchie,
  springbig,
}: {
  alpine: boolean;
  dutchie: boolean;
  springbig: boolean;
}): LoyaltyProvider {
  if (alpine) return 'Alpine IQ';
  if (dutchie) return 'Dutchie Loyalty';
  if (springbig) return 'Springbig';
  return null;
}

function getLoyaltyStatus({
  provider,
  requiresValidation,
  hasError,
  loading,
  hasRewards,
}: {
  provider: LoyaltyProvider;
  requiresValidation: boolean;
  hasError: boolean;
  loading: boolean;
  hasRewards: boolean;
}): KioskLoyaltyStatus {
  if (!provider) return 'unavailable';
  if (requiresValidation) return 'validation_required';
  if (hasError) return 'error';
  if (loading) return 'loading';
  return hasRewards ? 'available' : 'unavailable';
}

export function useLoyalty(): UseLoyaltyReturn {
  const Cart = useCart();
  const User = useUser();
  const isLoggedIn = useObserver(() => User.isLoggedIn);
  const isLoading = useObserver(() => User.loading);
  const birthday = useObserver(() => User.birthday);
  const phone = useObserver(() => User.phone);
  const cartSubtotal = useObserver(() => Cart.chargeSubtotal);
  const isUserLoggedIn = isLoggedIn && !isLoading;

  const { dispensary } = useDispensary();
  const isKioskProEnabled = useKioskPro();
  const isShowLoyaltyFlagEnabled = (dispensary?.storeSettings?.showLoyaltyRewards ?? false) || isKioskProEnabled;
  const isAlpineIQEnabled = dispensary?.consumerDispensaryIntegrations?.alpineiq?.enabled ?? false;
  const isDutchiePOSLoyaltyEnabled = dispensary?.consumerDispensaryIntegrations?.dutchieposloyalty?.enabled ?? false;
  const isSpringbigEnabled = dispensary?.consumerDispensaryIntegrations?.springbig?.enabled ?? false;

  const customerId = User?.id;
  const pin = User.getLoyaltyPin(dispensary?.id);

  const skip =
    !isShowLoyaltyFlagEnabled ||
    !isUserLoggedIn ||
    !phone ||
    (isAlpineIQEnabled && !pin) ||
    (isDutchiePOSLoyaltyEnabled && !customerId) ||
    (isSpringbigEnabled && !customerId);

  const { data, loading, hasError } = useGetRewards({
    dispensaryId: dispensary?.id,
    birthday,
    pin,
    phone,
    skip,
    customerId,
    cartSubtotal,
  });

  const rewardsData = data?.getRewardsV2;

  useEffect(() => {
    if (
      data &&
      (rewardsData?.auth?.pinConfirmed ||
        (isDutchiePOSLoyaltyEnabled && customerId) ||
        (isSpringbigEnabled && rewardsData?.userHasWallet))
    ) {
      setLoyaltyData({
        user: User,
        cart: Cart,
        data,
        dispensaryId: dispensary?.id,
      });
    }
  }, [data, rewardsData, isDutchiePOSLoyaltyEnabled, customerId, isSpringbigEnabled, User, Cart, dispensary?.id]);

  const loyaltyProvider = getLoyaltyProvider({
    alpine: isAlpineIQEnabled,
    dutchie: isDutchiePOSLoyaltyEnabled,
    springbig: isSpringbigEnabled,
  });
  const loyaltyStatus = getLoyaltyStatus({
    provider: loyaltyProvider,
    requiresValidation: isAlpineIQEnabled && !pin,
    hasError: !!hasError,
    loading,
    hasRewards: !!rewardsData,
  });

  return {
    loyaltyPoints: rewardsData?.balance ?? '0',
    loyaltyStatus,
    loyaltyProvider,
    isAlpineIQEnabled,
    isDutchiePOSLoyaltyEnabled,
    isSpringbigEnabled,
  };
}
